import React from 'react';
import { ArrowRight, Target, Heart, Award, Globe, TrendingUp } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { useNavigate } from 'react-router-dom';

const About = () => {
  const navigate = useNavigate();

  const values = [
    { icon: Heart, title: 'Founder-First', description: 'Every decision we make starts with what\'s best for the founder. Period.' },
    { icon: Target, title: 'Radical Transparency', description: 'No hidden agendas, no surprise fees, no false promises. Just honest guidance.' },
    { icon: Award, title: 'Quality Over Quantity', description: 'We don\'t take on every founder. We work with those we can genuinely help.' },
    { icon: Globe, title: 'Global Mindset', description: 'Local expertise with global reach. India to Silicon Valley, we cover it all.' }
  ];

  const milestones = [
    { year: '2022', title: 'FounderScale Capital Founded', description: 'Born out of frustration with traditional fundraising advisory' },
    { year: '2023', title: 'First 10 Successful Raises', description: 'Helped early-stage startups secure their first institutional rounds' },
    { year: '2024', title: 'Global Expansion', description: 'Extended operations to USA and Canada' },
    { year: '2026', title: '$5M+ Capital Facilitated', description: 'Crossed a meaningful milestone in capital raised for our portfolio' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <section className="relative py-24 bg-gradient-to-br from-emerald-600 via-green-600 to-emerald-700 overflow-hidden">
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6" data-testid="about-hero-title">
              We're Founders <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-200 to-emerald-100">Helping Founders</span>
            </h1>
            <p className="text-xl text-green-50 leading-relaxed">
              FounderScale Capital was born from a simple observation: fundraising advisory shouldn't feel like a black box. We bring transparency, honesty, and real expertise to help founders raise capital the right way.
            </p>
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Story</h2>
              <p className="text-xl text-emerald-600 font-semibold italic">From Pitch to Capital</p>
            </div>
            <div className="prose prose-lg max-w-none text-gray-700 space-y-6">
              <p className="text-lg leading-relaxed">
                After watching countless brilliant founders struggle with fundraising—not because their ideas weren't good enough, but because they didn't have access to the right network, the right preparation, or honest guidance—<strong className="text-emerald-700">Arshdeep and Sajid</strong> decided to build something different.
              </p>
              <p className="text-lg leading-relaxed">
                Traditional fundraising advisors charge huge retainers and rarely deliver. Marketplaces publicly display your raise details. Networks gatekeep introductions. <strong className="text-emerald-700">We knew there had to be a better way.</strong>
              </p>
              <p className="text-lg leading-relaxed">
                FounderScale Capital combines the best of all worlds: personalized advisory, curated investor access, transparent pricing, and most importantly—the honesty to tell you when we can or can't help. We work with founders at every stage, from ideation to pre-Series A, across every major sector.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-br from-emerald-50 to-green-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              What We <span className="text-emerald-600">Stand For</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">Our values guide every interaction</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {values.map((value, index) => (
              <Card key={index} className="border-2 border-gray-100 hover:border-emerald-300 transition-all duration-300 hover:shadow-xl hover:-translate-y-1 group bg-white" data-testid={`value-card-${index}`}>
                <CardContent className="p-8">
                  <div className="bg-gradient-to-br from-emerald-500 to-green-600 w-16 h-16 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                    <value.icon className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">{value.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{value.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Our <span className="text-emerald-600">Journey</span>
            </h2>
          </div>
          <div className="max-w-3xl mx-auto">
            {milestones.map((milestone, index) => (
              <div key={index} className="flex gap-6 mb-8 group">
                <div className="flex flex-col items-center">
                  <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-green-600 rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                    <TrendingUp className="h-7 w-7 text-white" />
                  </div>
                  {index < milestones.length - 1 && (
                    <div className="w-0.5 h-full bg-gradient-to-b from-emerald-300 to-transparent mt-4"></div>
                  )}
                </div>
                <div className="flex-1 pb-8">
                  <div className="text-emerald-600 font-bold text-sm mb-1">{milestone.year}</div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{milestone.title}</h3>
                  <p className="text-gray-600">{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-r from-emerald-600 to-green-600">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Let's Build Something Great Together</h2>
          <p className="text-xl text-green-50 mb-8 max-w-2xl mx-auto">
            Whether you're ideating, pre-seed, or pre-Series A—we're here to help you raise capital the right way.
          </p>
          <Button size="lg" onClick={() => navigate('/contact')} data-testid="about-cta-btn"
            className="bg-white text-emerald-700 hover:bg-green-50 text-lg px-12 py-6 shadow-2xl hover:shadow-white/50 transition-all duration-300 hover:scale-105 font-bold">
            Connect With Us<ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>
    </div>
  );
};

export default About;
